// Need to use the React-specific entry point to allow generating React hooks
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { baseQueryWithDecryption } from "../baseQueryWithEncryption";
export const getQueryStringFromObject = (option: {
  roleCode?: string;
  roleName?: string;
  deptName?: string;
  status?: string;
  addedBy?: string;
  createdAtStart?: string;
  createdAtEnd?: string;
  searchName?: string;
  page?: number;
  size?: number;
}) => {
  return Object.keys(option)
    .filter((key) => ![undefined, null, ""].includes(option[key]))
    .map((key) => `${key}=${option[key]}`)
    .join("&");
};

// Define a service using a base URL and expected endpoints
export const companyMembersApi = createApi({
  reducerPath: "companyMembersApi",
  tagTypes: ["users", "apiKeys"],
  baseQuery: baseQueryWithDecryption(import.meta.env.VITE_COMPANY_API_URL),
  endpoints: (builder) => ({
    getCompanyMembers: builder.query({
      query: ({
        searchName,
        roleCode,
        roleName,
        deptName,
        status,
        addedBy,
        createdAtStart,
        createdAtEnd,
        page,
        size,
      }) => {
        let queryString = `/user-mgmt/users?isGetAdmin=true&all=true`;
        if (searchName !== undefined) {
          queryString += `&searchName=${searchName.trim()}`;
        }
        if (roleCode !== undefined) {
          queryString += `&roleCode=${roleCode}`;
        }
        if (roleName !== undefined) {
          queryString += `&roleName=${roleName}`;
        }
        if (deptName !== undefined) {
          queryString += `&deptName=${deptName}`;
        }
        if (status !== undefined) {
          queryString += `&status=${status?.toUpperCase()}`;
        }
        if (addedBy !== undefined) {
          queryString += `&addedBy=${addedBy}`;
        }
        if (createdAtStart && createdAtEnd) {
          queryString += `&createdAtStart=${createdAtStart}&createdAtEnd=${createdAtEnd}`;
        }
        if (page !== undefined) {
          queryString += `&page=${page}`;
        }
        if (size !== undefined) {
          queryString += `&size=${size}`;
        }
        return queryString;
      },
      providesTags: ["users"],
    }),

    // getCompanyInvitedMembers: builder.query({
    // 	query: ({ roleCode, page, size }) => {
    // 		let prefix = `/team-member/invites`;
    // 		const queryStr = getQueryStringFromObject({ roleCode, page, size });
    // 		// return `/content-mgmt/data-hubs?${window.btoa(queryStr)}`;
    // 		return `${prefix}${queryStr && `?${queryStr}`}`;
    // 	},
    // 	providesTags: ['users'],
    // }),

    getCompanyInvitedMembers: builder.query({
      query: ({
        roleCode,
        roleName,
        deptName,
        status,
        addedBy,
        createdAtStart,
        createdAtEnd,
        searchName,
        page,
        size,
      }) => {
        let prefix = "/team-member/invites";

        // Build the query string dynamically using getQueryStringFromObject
        const queryStr = getQueryStringFromObject({
          roleCode,
          roleName,
          deptName,
          status: status?.toUpperCase(),
          addedBy,
          createdAtStart,
          createdAtEnd,
          searchName: searchName?.trim(),
          page,
          size,
        });

        return `${prefix}${queryStr && `?${queryStr}`}`;
      },
      providesTags: ["users"],
    }),

    updateLastTeam: builder.mutation({
      query: ({ ...body }) => ({
        url: `/user-mgmt/user/teams`,
        method: "PATCH",
        body: body,
      }),
    }),

    createUser: builder.mutation({
      query: (payload) => ({
        url: "/user-mgmt/create-user",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["users"],
    }),
    updateUser: builder.mutation({
      query: ({ userId, body }) => ({
        url: `/user-mgmt/edit-user/${userId}`,
        method: "PATCH",
        body: body,
      }),
      invalidatesTags: ["users"],
    }),
    deleteUser: builder.mutation({
      query: ({ userId }) => ({
        url: `/user-mgmt/delete-user/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["users"],
    }),
    getUserDetailsByUserId: builder.query({
      query: ({ userId }) => ({
        url: `/user-mgmt/details-user/${userId}`,
        method: "GET",
      }),
      providesTags: ["users"],
    }),
    generateClientCreds: builder.mutation({
      query: () => ({
        url: `user-mgmt/external/create`,
        method: "GET",
      }),
    }),
    deleteClientCredentials: builder.mutation({
      query: () => ({
        url: `user-mgmt/external/delete`,
        method: "DELETE",
      }),
    }),
    getApiKeys: builder.query({
      query: () => ({
        url: `api-key`,
        method: "GET",
      }),
      providesTags: ["apiKeys"],
    }),

    generateApiKey: builder.mutation({
      query: (deptName) => ({
        url: `api-key/generate`,
        method: "POST",
        body: {
          deptName,
        },
      }),
      invalidatesTags: ["apiKeys"],
    }),
    deleteApiKey: builder.mutation({
      query: ({ apiKeyId }) => ({
        url: `api-key/${apiKeyId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["apiKeys"],
    }),
    sendInvite: builder.mutation({
      query: (payload) => ({
        url: "/invite",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["users"],
    }),
  }),
});

export const {
  useGetCompanyMembersQuery,
  useGetCompanyInvitedMembersQuery,
  useLazyGetCompanyMembersQuery,
  useUpdateLastTeamMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useLazyGetUserDetailsByUserIdQuery,
  useDeleteClientCredentialsMutation,
  useGenerateClientCredsMutation,
  useDeleteApiKeyMutation,
  useGenerateApiKeyMutation,
  useGetApiKeysQuery,
  useSendInviteMutation,
} = companyMembersApi;
